import React from "react";
import { ArrowRight, Check, Minus } from "lucide-react";
import { AppHeader } from "../../../../components/layout/AppHeader";
import { ButtonLink } from "../../../../components/ui/button";
import { LandingFooter, CTASection, PricingSection } from "../landing";
import { FadeUp } from "../landing/FadeUp";
import { useLandingScrollToSection } from "./useLandingScrollToSection";
import type { ComparisonContent, RouteMap, UserDisplay } from "../../types";

type ComparisonPageProps = {
  comparison: ComparisonContent;
  loggedIn: boolean;
  hasBusiness: boolean;
  routes: RouteMap;
  csrfToken: string;
  page: string;
  userDisplay?: UserDisplay | null;
};

const AdvantageBadge: React.FC<{
  advantage: ComparisonContent["featureMatrix"][number]["advantage"];
}> = ({ advantage }) => {
  if (advantage === "tie") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
        <Minus className="h-3 w-3" /> Tie
      </span>
    );
  }
  return (
    <span
      className={
        advantage === "toolkit"
          ? "inline-flex items-center gap-1 rounded-full bg-premium-100 px-2 py-0.5 text-xs font-semibold text-premium-700"
          : "inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground"
      }
    >
      <Check className="h-3 w-3" />
      {advantage === "toolkit" ? "ToolKit" : "Competitor"}
    </span>
  );
};

const ComparisonHero: React.FC<{
  comparison: ComparisonContent;
  routes: RouteMap;
}> = ({ comparison, routes }) => (
  <section className="bg-background pt-20 pb-12 sm:pt-28">
    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
      <span className="inline-flex items-center gap-2 rounded-full border border-premium-500/40 bg-premium-50 px-3 py-1 text-sm font-semibold text-premium-700">
        Comparison
      </span>
      <h1 className="mt-5 font-display text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl">
        ToolKit vs {comparison.competitorName}
      </h1>
      <div
        className="prose-seo mt-6 max-w-none text-lg leading-relaxed text-muted-foreground"
        dangerouslySetInnerHTML={{ __html: comparison.summaryHtml }}
      />
      <div className="mt-8 flex flex-col gap-4 sm:flex-row">
        <ButtonLink to={routes.signup} size="lg">
          Try ToolKit free
          <ArrowRight className="ml-2 h-5 w-5" />
        </ButtonLink>
      </div>
    </div>
  </section>
);

const ComparisonMatrix: React.FC<{ comparison: ComparisonContent }> = ({
  comparison,
}) => (
  <section className="bg-card py-16">
    <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
      <FadeUp>
        <h2 className="font-display text-3xl font-bold text-foreground sm:text-4xl">
          Feature comparison
        </h2>
      </FadeUp>
      <div className="mt-8 overflow-hidden rounded-xl border border-border">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted text-left text-sm font-semibold text-foreground">
            <tr>
              <th scope="col" className="px-4 py-3">
                Feature
              </th>
              <th scope="col" className="px-4 py-3">
                ToolKit
              </th>
              <th scope="col" className="px-4 py-3">
                {comparison.competitorName}
              </th>
              <th scope="col" className="px-4 py-3">
                Edge
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border bg-background text-sm">
            {comparison.featureMatrix.map((row) => (
              <tr key={row.feature}>
                <td className="px-4 py-3 font-medium text-foreground">
                  {row.feature}
                </td>
                <td className="px-4 py-3 text-muted-foreground">
                  {row.toolkit}
                </td>
                <td className="px-4 py-3 text-muted-foreground">
                  {row.competitor}
                </td>
                <td className="px-4 py-3">
                  <AdvantageBadge advantage={row.advantage} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </section>
);

export const ComparisonPage: React.FC<ComparisonPageProps> = ({
  comparison,
  loggedIn,
  hasBusiness,
  routes,
  csrfToken,
  page,
  userDisplay,
}) => {
  const scrollToSection = useLandingScrollToSection();

  return (
    <div className="min-h-screen overflow-x-hidden bg-background text-foreground">
      <AppHeader
        variant="landing"
        loggedIn={loggedIn}
        hasBusiness={hasBusiness}
        routes={routes}
        csrfToken={csrfToken}
        page={page}
        userDisplay={userDisplay}
        scrollToSection={scrollToSection}
      />
      <ComparisonHero comparison={comparison} routes={routes} />
      <ComparisonMatrix comparison={comparison} />
      <PricingSection routes={routes} />
      <CTASection routes={routes} />
      <LandingFooter
        loggedIn={loggedIn}
        hasBusiness={hasBusiness}
        routes={routes}
        scrollToSection={scrollToSection}
      />
    </div>
  );
};
